import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Card,
  Steps,
  Button,
  Typography,
  Form,
  Input,
  Checkbox,
  Select,
  Space,
  Spin,
  message,
  Alert,
  Divider,
  Row,
  Col,
  Tag,
  List,
  Result,
} from 'antd';
import {
  FileTextOutlined,
  CheckCircleOutlined,
  TeamOutlined,
  SendOutlined,
  InfoCircleOutlined,
  MailOutlined,
  UserOutlined,
  PlusOutlined,
  DeleteOutlined,
} from '@ant-design/icons';
import { apiService } from '../../services/api';

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;

interface WizardSession {
  id: number;
  wizardToken: string;
  wizardType: string;
  wizardStatus: string;
  currentStep: number;
  wizardData: any;
  expiresAt: string;
  project: {
    id: number;
    pid: string;
    projectType: string;
    scope: string;
    client?: {
      id: number;
      name: string;
    };
    currentScopeVersion?: {
      id: number;
      versionNumber: number;
      scopeContent: string;
      versionStatus: string;
      acknowledgedByClient: boolean;
    };
    distributionGroups?: Array<{
      id: number;
      groupName: string;
      description: string;
      contacts: Array<{
        id: number;
        name: string;
        email: string;
        role: string;
      }>;
    }>;
    requirementChecklist?: {
      id: number;
      status: string;
      testEnvironmentDetails: string;
      accessCredentialsInfo: string;
      scopeConfirmation: boolean;
      additionalNotes: string;
    };
  };
}

interface Contact {
  name: string;
  email: string;
  role: string;
}

interface NewDistributionGroup {
  groupName: string;
  description: string;
  contacts: Contact[];
}

interface WizardFormData {
  // Step 1: Requirements
  testEnvironmentDetails: string;
  accessCredentialsInfo: string;
  additionalNotes: string;
  // Step 2: Scope
  scopeConfirmed: boolean;
  // Step 3: Distribution
  selectedDistributionGroups: number[];
  newContacts: Contact[];
}

const ReportInfoPanel: React.FC<{ title: string; description: string }> = ({ title, description }) => (
  <Alert
    type="info"
    icon={<InfoCircleOutlined />}
    message={title}
    description={description}
    style={{ marginBottom: 16 }}
  />
);

export const ProjectOnboardingWizard: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [session, setSession] = useState<WizardSession | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [allDistributionGroups, setAllDistributionGroups] = useState<any[]>([]);
  const [wasAlreadyCompleted, setWasAlreadyCompleted] = useState(false);
  
  const [requirementsForm] = Form.useForm();
  const [scopeForm] = Form.useForm();
  const [distributionForm] = Form.useForm();
  
  const [formData, setFormData] = useState<WizardFormData>({
    testEnvironmentDetails: '',
    accessCredentialsInfo: '',
    additionalNotes: '',
    scopeConfirmed: false,
    selectedDistributionGroups: [],
    newContacts: [],
  });
  
  const [newContacts, setNewContacts] = useState<Contact[]>([]);
  const [newDistributionGroups, setNewDistributionGroups] = useState<NewDistributionGroup[]>([]);

  const validateToken = useCallback(async () => {
    if (!token) {
      setError('No wizard token provided');
      setLoading(false);
      return;
    }

    try {
      const result = await apiService.validateWizardToken(token);
      
      if (!result.valid) {
        // Check if wizard was already completed - show success state instead of error
        if ((result as any).completed && result.session) {
          setSession(result.session);
          setWasAlreadyCompleted(true);
          setCurrentStep(4); // Show completion step
          setLoading(false);
          return;
        }
        setError(result.error || 'Invalid or expired wizard link');
        setLoading(false);
        return;
      }

      setSession(result.session);
      setCurrentStep(result.session.currentStep || 0);
      
      // Pre-populate form data from existing data
      const existingData = result.session.wizardData || {};
      const checklist = result.session.project?.requirementChecklist;
      
      setFormData({
        testEnvironmentDetails: existingData.testEnvironmentDetails || checklist?.testEnvironmentDetails || '',
        accessCredentialsInfo: existingData.accessCredentialsInfo || checklist?.accessCredentialsInfo || '',
        additionalNotes: existingData.additionalNotes || checklist?.additionalNotes || '',
        scopeConfirmed: existingData.scopeConfirmed || false,
        selectedDistributionGroups: existingData.selectedDistributionGroups || 
          (result.session.project?.distributionGroups?.map((g: any) => g.id) || []),
        newContacts: existingData.newContacts || [],
      });
      
      setNewContacts(existingData.newContacts || []);
      
      // Set form values
      requirementsForm.setFieldsValue({
        testEnvironmentDetails: existingData.testEnvironmentDetails || checklist?.testEnvironmentDetails || '',
        accessCredentialsInfo: existingData.accessCredentialsInfo || checklist?.accessCredentialsInfo || '',
        additionalNotes: existingData.additionalNotes || checklist?.additionalNotes || '',
      });
      
      scopeForm.setFieldsValue({
        scopeConfirmed: existingData.scopeConfirmed || false,
      });
      
      distributionForm.setFieldsValue({
        selectedDistributionGroups: existingData.selectedDistributionGroups || 
          (result.session.project?.distributionGroups?.map((g: any) => g.id) || []),
      });

      // Fetch distribution groups filtered by client
      try {
        const clientId = result.session.project?.client?.id;
        if (clientId) {
          const groupsResponse = await apiService.getDistributionGroupsByClient(clientId);
          setAllDistributionGroups(groupsResponse.data || []);
        } else {
          // Fallback to all groups if no client (shouldn't happen normally)
          const groupsResponse = await apiService.getAllDistributionGroups();
          setAllDistributionGroups(groupsResponse.data || []);
        }
      } catch (err) {
        console.error('Failed to fetch distribution groups:', err);
      }
      
    } catch (err: any) {
      setError(err.message || 'Failed to validate wizard token');
    } finally {
      setLoading(false);
    }
  }, [token, requirementsForm, scopeForm, distributionForm]);

  useEffect(() => {
    validateToken();
  }, [validateToken]);

  const saveProgress = async (step: number, data: Partial<WizardFormData>) => {
    if (!token) return;
    
    const updatedData = { ...formData, ...data };
    setFormData(updatedData);
    
    try {
      await apiService.saveWizardProgress(token, step, updatedData);
    } catch (err) {
      console.error('Failed to save progress:', err);
    }
  };

  const handleNext = async () => {
    try {
      if (currentStep === 0) {
        const values = await requirementsForm.validateFields();
        await saveProgress(currentStep + 1, values);
      } else if (currentStep === 1) {
        const values = await scopeForm.validateFields();
        if (!values.scopeConfirmed) {
          message.warning('Please confirm the scope before proceeding');
          return;
        }
        await saveProgress(currentStep + 1, values);
      } else if (currentStep === 2) {
        const values = await distributionForm.validateFields();
        await saveProgress(currentStep + 1, { ...values, newContacts });
      }
      
      setCurrentStep(currentStep + 1);
    } catch (err) {
      message.error('Please fill in all required fields');
    }
  };

  const handlePrev = () => {
    setCurrentStep(currentStep - 1);
  };

  const handleComplete = async () => {
    if (!token || !session) return;
    
    setSubmitting(true);
    try {
      const finalData = {
        requirementChecklist: {
          testEnvironmentDetails: formData.testEnvironmentDetails,
          accessCredentialsInfo: formData.accessCredentialsInfo,
          additionalNotes: formData.additionalNotes,
          scopeConfirmation: formData.scopeConfirmed,
        },
        scopeConfirmed: formData.scopeConfirmed,
        distributionGroupIds: formData.selectedDistributionGroups,
        newContacts: newContacts.filter(c => c.name && c.email),
        newDistributionGroups: newDistributionGroups.filter(g => g.groupName && g.contacts.some(c => c.email)),
      };
      
      await apiService.completeWizard(token, finalData);
      message.success('Project setup completed successfully!');
      setCurrentStep(4); // Move to completion step
    } catch (err: any) {
      message.error(err.message || 'Failed to complete wizard');
    } finally {
      setSubmitting(false);
    }
  };

  const addNewContact = () => {
    setNewContacts([...newContacts, { name: '', email: '', role: 'other' }]);
  };

  const updateContact = (index: number, field: keyof Contact, value: string) => {
    const updated = [...newContacts];
    updated[index] = { ...updated[index], [field]: value };
    setNewContacts(updated);
  };

  const removeContact = (index: number) => {
    setNewContacts(newContacts.filter((_, i) => i !== index));
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '100px' }}>
        <Spin size="large" />
        <div style={{ marginTop: '16px' }}>
          <Text>Validating wizard access...</Text>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ maxWidth: 600, margin: '50px auto', padding: '20px' }}>
        <Result
          status="error"
          title="Unable to Access Wizard"
          subTitle={error}
          extra={[
            <Button type="primary" key="login" onClick={() => navigate('/login')}>
              Go to Login
            </Button>,
            <Button key="projects" onClick={() => navigate('/projects')}>
              View Projects
            </Button>,
          ]}
        />
      </div>
    );
  }

  if (!session) {
    return null;
  }

  const steps = [
    {
      title: 'Requirements',
      icon: <FileTextOutlined />,
    },
    {
      title: 'Scope',
      icon: <CheckCircleOutlined />,
    },
    {
      title: 'Distribution',
      icon: <TeamOutlined />,
    },
    {
      title: 'Submit',
      icon: <SendOutlined />,
    },
  ];

  const renderRequirementsStep = () => (
    <Row gutter={24}>
      <Col xs={24} lg={16}>
        <Card title="Test Environment & Requirements" bordered={false}>
          <Form form={requirementsForm} layout="vertical">
            <Form.Item
              name="testEnvironmentDetails"
              label="Test Environment Details"
              rules={[{ required: true, message: 'Please provide test environment details' }]}
            >
              <TextArea
                rows={6}
                placeholder="Describe the test environment, including:
- URLs/IP addresses to be tested
- Any staging/test environments available
- VPN or access requirements
- Testing windows/restrictions"
              />
            </Form.Item>

            <Form.Item
              name="accessCredentialsInfo"
              label="Access Credentials Information"
              extra="This information is stored securely and will not appear in the final report"
            >
              <TextArea
                rows={4}
                placeholder="Provide any credentials, API keys, or access information needed for testing (optional - can be shared securely later)"
              />
            </Form.Item>

            <Form.Item
              name="additionalNotes"
              label="Additional Notes"
            >
              <TextArea
                rows={4}
                placeholder="Any additional information, special requirements, or concerns"
              />
            </Form.Item>
          </Form>
        </Card>
      </Col>
      <Col xs={24} lg={8}>
        <ReportInfoPanel
          title="Where This Information Appears"
          description="The test environment details you provide will appear in the 'Scope and Environment' section of your final penetration testing report. This helps establish the testing context and boundaries."
        />
        <Card size="small" style={{ marginTop: 16 }}>
          <Title level={5}>
            <InfoCircleOutlined style={{ marginRight: 8 }} />
            Tips
          </Title>
          <ul style={{ paddingLeft: 20, margin: 0 }}>
            <li>Be specific about which systems should be tested</li>
            <li>Mention any systems that should NOT be tested</li>
            <li>Include any time restrictions for testing</li>
            <li>Credentials can be shared later through secure channels</li>
          </ul>
        </Card>
      </Col>
    </Row>
  );

  const renderScopeStep = () => (
    <Row gutter={24}>
      <Col xs={24} lg={16}>
        <Card title="Project Scope Confirmation" bordered={false}>
          <Alert
            type="warning"
            message="Please Review Carefully"
            description="The scope below defines exactly what will be tested during the penetration test. Please review and confirm that this accurately represents your expectations."
            style={{ marginBottom: 24 }}
          />
          
          <Card
            type="inner"
            title={
              <span>
                Current Scope
                {session.project.currentScopeVersion && (
                  <Tag color="blue" style={{ marginLeft: 8 }}>
                    v{session.project.currentScopeVersion.versionNumber}
                  </Tag>
                )}
              </span>
            }
            style={{ backgroundColor: 'var(--ant-color-bg-container)' }}
          >
            <div
              style={{ 
                maxHeight: 400, 
                overflow: 'auto', 
                padding: 16, 
                backgroundColor: 'var(--ant-color-fill-alter, rgba(0, 0, 0, 0.02))',
                borderRadius: 4,
                color: 'var(--ant-color-text)',
              }}
              dangerouslySetInnerHTML={{ 
                __html: session.project.currentScopeVersion?.scopeContent || session.project.scope || '<em>No scope defined yet</em>' 
              }}
            />
          </Card>
          
          <Form form={scopeForm} layout="vertical" style={{ marginTop: 24 }}>
            <Form.Item
              name="scopeConfirmed"
              valuePropName="checked"
              rules={[
                {
                  validator: (_, value) =>
                    value ? Promise.resolve() : Promise.reject(new Error('You must confirm the scope')),
                },
              ]}
            >
              <Checkbox>
                <Text strong>
                  I confirm that I have reviewed the scope above and it accurately represents the systems 
                  and boundaries for this penetration test.
                </Text>
              </Checkbox>
            </Form.Item>
          </Form>
        </Card>
      </Col>
      <Col xs={24} lg={8}>
        <ReportInfoPanel
          title="Why Scope Confirmation Matters"
          description="The confirmed scope defines the legal boundaries of testing. Only systems within the scope will be tested. This confirmation will be documented in the 'Testing Scope' section of your final report."
        />
        <Card size="small" style={{ marginTop: 16 }}>
          <Title level={5}>
            <InfoCircleOutlined style={{ marginRight: 8 }} />
            What if the scope is incorrect?
          </Title>
          <Paragraph style={{ fontSize: 13 }}>
            If the scope does not accurately reflect what you need tested, please contact your 
            Citadelo representative before confirming. Scope changes may affect timeline and pricing.
          </Paragraph>
        </Card>
      </Col>
    </Row>
  );

  const addNewDistributionGroup = () => {
    setNewDistributionGroups([...newDistributionGroups, { groupName: '', description: '', contacts: [{ name: '', email: '', role: 'technical' }] }]);
  };

  const updateDistributionGroup = (groupIndex: number, field: keyof NewDistributionGroup, value: any) => {
    const updated = [...newDistributionGroups];
    updated[groupIndex] = { ...updated[groupIndex], [field]: value };
    setNewDistributionGroups(updated);
  };

  const addContactToGroup = (groupIndex: number) => {
    const updated = [...newDistributionGroups];
    updated[groupIndex].contacts.push({ name: '', email: '', role: 'technical' });
    setNewDistributionGroups(updated);
  };

  const updateGroupContact = (groupIndex: number, contactIndex: number, field: keyof Contact, value: string) => {
    const updated = [...newDistributionGroups];
    updated[groupIndex].contacts[contactIndex] = { ...updated[groupIndex].contacts[contactIndex], [field]: value };
    setNewDistributionGroups(updated);
  };

  const removeGroupContact = (groupIndex: number, contactIndex: number) => {
    const updated = [...newDistributionGroups];
    updated[groupIndex].contacts = updated[groupIndex].contacts.filter((_, i) => i !== contactIndex);
    setNewDistributionGroups(updated);
  };

  const removeDistributionGroup = (index: number) => {
    setNewDistributionGroups(newDistributionGroups.filter((_, i) => i !== index));
  };

  const renderDistributionStep = () => (
    <Row gutter={24}>
      <Col xs={24} lg={16}>
        <Card title="Report Distribution List" bordered={false}>
          <Paragraph>
            Select who should receive the final penetration testing report. You can choose from 
            existing distribution groups or create new ones.
          </Paragraph>
          
          <Form form={distributionForm} layout="vertical">
            <Form.Item
              name="selectedDistributionGroups"
              label="Select Existing Distribution Groups"
            >
              <Select
                mode="multiple"
                placeholder="Select groups to receive the report"
                style={{ width: '100%' }}
                optionFilterProp="children"
              >
                {allDistributionGroups.map(group => (
                  <Select.Option key={group.id} value={group.id}>
                    <Space>
                      <TeamOutlined />
                      {group.groupName}
                      {group.contacts?.length > 0 && (
                        <Tag style={{ fontSize: 12 }}>{group.contacts.length} contacts</Tag>
                      )}
                    </Space>
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Form>
          
          <Divider />
          
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <Title level={5} style={{ margin: 0 }}>Create New Distribution Group</Title>
              <Button type="primary" icon={<PlusOutlined />} onClick={addNewDistributionGroup}>
                Create Group
              </Button>
            </div>
            
            {newDistributionGroups.length === 0 ? (
              <Text type="secondary">No new groups created. Click "Create Group" to add one.</Text>
            ) : (
              newDistributionGroups.map((group, groupIndex) => (
                <Card 
                  key={groupIndex} 
                  size="small" 
                  style={{ marginBottom: 16 }}
                  title={
                    <Input
                      placeholder="Group Name (e.g., Technical Team)"
                      value={group.groupName}
                      onChange={(e) => updateDistributionGroup(groupIndex, 'groupName', e.target.value)}
                      style={{ maxWidth: 300 }}
                    />
                  }
                  extra={
                    <Button type="text" danger icon={<DeleteOutlined />} onClick={() => removeDistributionGroup(groupIndex)}>
                      Remove
                    </Button>
                  }
                >
                  <Input
                    placeholder="Description (optional)"
                    value={group.description}
                    onChange={(e) => updateDistributionGroup(groupIndex, 'description', e.target.value)}
                    style={{ marginBottom: 12 }}
                  />
                  <Text strong style={{ display: 'block', marginBottom: 8 }}>Contacts:</Text>
                  {group.contacts.map((contact, contactIndex) => (
                    <div key={contactIndex} style={{ display: 'flex', gap: 8, marginBottom: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                      <Input
                        placeholder="Name"
                        prefix={<UserOutlined />}
                        value={contact.name}
                        onChange={(e) => updateGroupContact(groupIndex, contactIndex, 'name', e.target.value)}
                        style={{ width: 140 }}
                      />
                      <Input
                        placeholder="Email"
                        prefix={<MailOutlined />}
                        value={contact.email}
                        onChange={(e) => updateGroupContact(groupIndex, contactIndex, 'email', e.target.value)}
                        style={{ width: 180 }}
                      />
                      <Select
                        value={contact.role}
                        onChange={(value) => updateGroupContact(groupIndex, contactIndex, 'role', value)}
                        style={{ width: 110 }}
                      >
                        <Select.Option value="technical">Technical</Select.Option>
                        <Select.Option value="management">Management</Select.Option>
                        <Select.Option value="executive">Executive</Select.Option>
                        <Select.Option value="other">Other</Select.Option>
                      </Select>
                      {group.contacts.length > 1 && (
                        <Button type="text" danger icon={<DeleteOutlined />} onClick={() => removeGroupContact(groupIndex, contactIndex)} />
                      )}
                    </div>
                  ))}
                  <Button type="dashed" size="small" icon={<PlusOutlined />} onClick={() => addContactToGroup(groupIndex)}>
                    Add Contact
                  </Button>
                </Card>
              ))
            )}
          </div>
          
          <Divider />
          
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <Title level={5} style={{ margin: 0 }}>Add Individual Contacts</Title>
              <Button type="dashed" icon={<PlusOutlined />} onClick={addNewContact}>
                Add Contact
              </Button>
            </div>
            
            {newContacts.length === 0 ? (
              <Text type="secondary">No additional individual contacts added</Text>
            ) : (
              <List
                dataSource={newContacts}
                renderItem={(contact, index) => (
                  <List.Item
                    actions={[
                      <Button
                        key="delete"
                        type="text"
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => removeContact(index)}
                      />,
                    ]}
                  >
                    <Space style={{ width: '100%' }} wrap>
                      <Input
                        placeholder="Name"
                        prefix={<UserOutlined />}
                        value={contact.name}
                        onChange={(e) => updateContact(index, 'name', e.target.value)}
                        style={{ width: 150 }}
                      />
                      <Input
                        placeholder="Email"
                        prefix={<MailOutlined />}
                        value={contact.email}
                        onChange={(e) => updateContact(index, 'email', e.target.value)}
                        style={{ width: 200 }}
                      />
                      <Select
                        value={contact.role}
                        onChange={(value) => updateContact(index, 'role', value)}
                        style={{ width: 120 }}
                      >
                        <Select.Option value="technical">Technical</Select.Option>
                        <Select.Option value="management">Management</Select.Option>
                        <Select.Option value="executive">Executive</Select.Option>
                        <Select.Option value="other">Other</Select.Option>
                      </Select>
                    </Space>
                  </List.Item>
                )}
              />
            )}
          </div>
        </Card>
      </Col>
      <Col xs={24} lg={8}>
        <ReportInfoPanel
          title="Report Distribution"
          description="The people and groups you select will receive the final penetration testing report. Reports contain sensitive security information and should only be shared with authorized personnel."
        />
        <Card size="small" style={{ marginTop: 16 }}>
          <Title level={5}>
            <InfoCircleOutlined style={{ marginRight: 8 }} />
            Contact Roles
          </Title>
          <ul style={{ paddingLeft: 20, margin: 0, fontSize: 13 }}>
            <li><strong>Technical:</strong> Receives full technical details</li>
            <li><strong>Management:</strong> Receives executive summary</li>
            <li><strong>Executive:</strong> Receives high-level overview</li>
          </ul>
        </Card>
      </Col>
    </Row>
  );

  const renderSummaryStep = () => (
    <Row gutter={24}>
      <Col xs={24} lg={16}>
        <Card title="Review & Submit" bordered={false}>
          <Alert
            type="info"
            message="Please review your information before submitting"
            style={{ marginBottom: 24 }}
          />
          
          <Card type="inner" title="Requirements Summary" style={{ marginBottom: 16 }}>
            <Paragraph>
              <Text strong>Test Environment:</Text>
              <br />
              <Text type="secondary" style={{ whiteSpace: 'pre-wrap' }}>
                {formData.testEnvironmentDetails || 'Not provided'}
              </Text>
            </Paragraph>
            {formData.additionalNotes && (
              <Paragraph>
                <Text strong>Additional Notes:</Text>
                <br />
                <Text type="secondary">{formData.additionalNotes}</Text>
              </Paragraph>
            )}
          </Card>
          
          <Card type="inner" title="Scope Confirmation" style={{ marginBottom: 16 }}>
            <Tag color={formData.scopeConfirmed ? 'green' : 'red'}>
              {formData.scopeConfirmed ? 'Confirmed' : 'Not Confirmed'}
            </Tag>
          </Card>
          
          <Card type="inner" title="Distribution List">
            <Text strong>Selected Existing Groups: </Text>
            {formData.selectedDistributionGroups.length > 0 ? (
              <Space wrap style={{ marginTop: 8 }}>
                {formData.selectedDistributionGroups.map(id => {
                  const group = allDistributionGroups.find(g => g.id === id);
                  return group ? (
                    <Tag key={id} icon={<TeamOutlined />}>{group.groupName}</Tag>
                  ) : null;
                })}
              </Space>
            ) : (
              <Text type="secondary">No existing groups selected</Text>
            )}
            
            {newDistributionGroups.filter(g => g.groupName).length > 0 && (
              <>
                <Divider />
                <Text strong>New Groups to Create: </Text>
                <Space wrap direction="vertical" style={{ marginTop: 8, width: '100%' }}>
                  {newDistributionGroups.filter(g => g.groupName).map((group, i) => (
                    <Tag key={i} icon={<TeamOutlined />} color="blue">
                      {group.groupName} ({group.contacts.filter(c => c.email).length} contacts)
                    </Tag>
                  ))}
                </Space>
              </>
            )}
            
            {newContacts.length > 0 && (
              <>
                <Divider />
                <Text strong>Individual Contacts: </Text>
                <Space wrap style={{ marginTop: 8 }}>
                  {newContacts.filter(c => c.name && c.email).map((contact, i) => (
                    <Tag key={i} icon={<UserOutlined />}>
                      {contact.name} ({contact.email})
                    </Tag>
                  ))}
                </Space>
              </>
            )}
          </Card>
        </Card>
      </Col>
      <Col xs={24} lg={8}>
        <Alert
          type="success"
          message="Ready to Submit"
          description="Once you submit, our team will be notified and will begin preparing for your penetration test based on the information provided."
          style={{ marginBottom: 16 }}
        />
        <Card size="small">
          <Title level={5}>What happens next?</Title>
          <ol style={{ paddingLeft: 20, margin: 0, fontSize: 13 }}>
            <li>Our team reviews your submission</li>
            <li>We may contact you for clarifications</li>
            <li>Testing begins on the scheduled date</li>
            <li>Report delivered to distribution list</li>
          </ol>
        </Card>
      </Col>
    </Row>
  );

  const renderCompletionStep = () => (
    <Result
      status="success"
      title={wasAlreadyCompleted ? "Project Setup Already Complete" : "Project Setup Complete!"}
      subTitle={wasAlreadyCompleted 
        ? `The setup for project ${session.project.pid} has already been completed. No further action is needed.`
        : `Thank you for completing the setup for project ${session.project.pid}. Our team will review your submission and begin preparations.`
      }
      extra={[
        <Button type="primary" key="projects" onClick={() => navigate('/projects')}>
          View My Projects
        </Button>,
      ]}
    />
  );

  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return renderRequirementsStep();
      case 1:
        return renderScopeStep();
      case 2:
        return renderDistributionStep();
      case 3:
        return renderSummaryStep();
      case 4:
        return renderCompletionStep();
      default:
        return null;
    }
  };

  return (
    <div style={{ maxWidth: 1200, margin: '0 auto', padding: '24px' }}>
      <Card style={{ marginBottom: 24 }}>
        <Title level={3} style={{ marginBottom: 8 }}>
          Project Setup Wizard
        </Title>
        <Text type="secondary">
          {session.project.client?.name} - {session.project.pid}
        </Text>
      </Card>

      {currentStep < 4 && (
        <Card style={{ marginBottom: 24 }}>
          <Steps current={currentStep} items={steps} />
        </Card>
      )}

      <div style={{ marginBottom: 24 }}>
        {renderStepContent()}
      </div>

      {currentStep < 4 && (
        <Card>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <Button onClick={handlePrev} disabled={currentStep === 0}>
              Previous
            </Button>
            <Space>
              {currentStep < 3 ? (
                <Button type="primary" onClick={handleNext}>
                  Next
                </Button>
              ) : (
                <Button 
                  type="primary" 
                  onClick={handleComplete} 
                  loading={submitting}
                  icon={<SendOutlined />}
                >
                  Complete Setup
                </Button>
              )}
            </Space>
          </div>
        </Card>
      )}
    </div>
  );
};
